import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { eoiAPI } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PROPERTY_TYPES = ['house', 'apartment', 'townhouse', 'land', 'rural', 'commercial', 'industrial'];
const CONDITIONS = ['any', 'move-in-ready', 'renovated', 'new-build'];
const AU_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'];
const FEATURES = ['Pool', 'Garage', 'Garden', 'Solar', 'Air Conditioning', 'Central Heating', 'Patio/Deck', 'Spa', 'Gym', 'Study', 'Shed', 'Granny Flat'];

// Status config
const STATUS_CFG = {
  new:               { label: 'New',              color: '#888',    bg: '#f5f5f5' },
  interested:        { label: 'Interested',        color: '#1a5baa', bg: '#f0f7ff' },
  details_requested: { label: 'Details Requested', color: '#a06000', bg: '#fff8e8' },
  negotiating:       { label: 'Negotiating',       color: '#2d6a2d', bg: '#e8f4e8' },
  closed:            { label: 'Closed',            color: '#c0392b', bg: '#fff0f0' },
};

const fmt = (n) => n == null ? '—' : `$${Number(n).toLocaleString()}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
const fmtTime = (s) => s ? new Date(s).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';

function Chip({ label, color = '#e8f4e8', textColor = '#2d6a2d' }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 12,
      background: color, color: textColor, fontSize: 11, fontWeight: 600, marginRight: 4,
    }}>{label}</span>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.new;
  return <Chip label={cfg.label} color={cfg.bg} textColor={cfg.color} />;
}

// ── Match Conversation Panel ──────────────────────────────────────────────────
function MatchConversationPanel({ eoi, onClose, onMatchUpdated }) {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgBody, setMsgBody] = useState('');
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  const isBuyer = eoi.is_mine;

  // Load all matches for this EOI
  const loadMatches = useCallback(async () => {
    try {
      const r = await eoiAPI.listMatches(eoi.id);
      setMatches(r.data);
      if (r.data.length > 0 && !selected) setSelected(r.data[0]);
    } finally {
      setLoadingMatches(false);
    }
  }, [eoi.id]);

  // Load messages for selected match
  const loadMessages = useCallback(async () => {
    if (!selected) return;
    setLoadingMsgs(true);
    try {
      const r = await eoiAPI.listMessages(eoi.id, selected.id);
      setMessages(r.data);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } finally {
      setLoadingMsgs(false);
    }
  }, [eoi.id, selected?.id]);

  useEffect(() => { loadMatches(); }, [loadMatches]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  const handleSelectMatch = (m) => {
    setSelected(m);
    setMessages([]);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!msgBody.trim() || !selected) return;
    setSending(true);
    try {
      const r = await eoiAPI.sendMessage(eoi.id, selected.id, msgBody.trim());
      setMessages(prev => [...prev, r.data]);
      setMsgBody('');
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch {}
    setSending(false);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selected) return;
    try {
      const r = await eoiAPI.updateMatchStatus(eoi.id, selected.id, { status: newStatus });
      setSelected(r.data);
      setMatches(prev => prev.map(m => m.id === r.data.id ? r.data : m));
      onMatchUpdated?.();
    } catch {}
  };

  // Buyer action buttons based on current status
  const buyerActions = () => {
    if (!isBuyer || !selected) return null;
    const s = selected.status;
    const actions = [];
    if (s === 'new') {
      actions.push({ label: 'Show Interest', status: 'interested', color: '#1a5baa', bg: '#f0f7ff' });
      actions.push({ label: 'Request Details', status: 'details_requested', color: '#a06000', bg: '#fff8e8' });
    }
    if (s === 'interested') {
      actions.push({ label: 'Request Details', status: 'details_requested', color: '#a06000', bg: '#fff8e8' });
      actions.push({ label: 'Start Negotiating', status: 'negotiating', color: '#2d6a2d', bg: '#e8f4e8' });
    }
    if (s === 'details_requested') {
      actions.push({ label: 'Start Negotiating', status: 'negotiating', color: '#2d6a2d', bg: '#e8f4e8' });
    }
    if (s !== 'closed') {
      actions.push({ label: 'Withdraw Interest', status: 'closed', color: '#c0392b', bg: '#fff0f0' });
    }
    return actions;
  };

  const actions = buyerActions();

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 1000, width: '95%' }}>
        {/* Header */}
        <div className="modal-head" style={{
          background: '#2d6a2d', color: '#fff',
          borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>Property Matches</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>"{eoi.title}"</div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
            borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14,
          }}>✕ Close</button>
        </div>

        <div className="master-detail">

          {/* Left — Match list */}
          <div className="master-panel" style={{ background: '#fafafa' }}>
            {loadingMatches && (
              <div style={{ padding: 24, color: '#888', textAlign: 'center', fontSize: 13 }}>Loading…</div>
            )}
            {!loadingMatches && matches.length === 0 && (
              <div style={{ padding: 24, color: '#888', textAlign: 'center', fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
                No responses yet
              </div>
            )}
            {matches.map(m => (
              <div
                key={m.id}
                onClick={() => handleSelectMatch(m)}
                style={{
                  padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                  background: selected?.id === m.id ? '#e8f4e8' : '#fff',
                  borderLeft: selected?.id === m.id ? '3px solid #2d6a2d' : '3px solid transparent',
                  transition: 'background 0.1s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a' }}>
                    {m.responder_name || 'Responder'}
                  </div>
                  {m.unread_messages > 0 && (
                    <span style={{
                      background: '#2d6a2d', color: '#fff', borderRadius: 10,
                      padding: '1px 7px', fontSize: 10, fontWeight: 700,
                    }}>{m.unread_messages}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  {m.responder_role} {m.responder_email && `· ${m.responder_email}`}
                </div>
                {m.property_address && (
                  <div style={{ fontSize: 12, color: '#555', marginTop: 5, fontStyle: 'italic' }}>
                    {m.property_address}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                  <StatusBadge status={m.status} />
                  {m.asking_price && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#2d6a2d' }}>{fmt(m.asking_price)}</span>
                  )}
                </div>
                {m.total_messages > 0 && (
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                    💬 {m.total_messages} message{m.total_messages !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right — Match detail + chat */}
          <div className="detail-panel">
          {!selected ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#aaa' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>👈</div>
                <div>Select a match to view details</div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

              {/* Property detail card */}
              <div style={{
                padding: '16px 20px', borderBottom: '1px solid #f0f0f0',
                background: '#fff', overflowY: 'auto', maxHeight: 280,
              }}>
                {/* Responder info + Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 20, background: '#2d6a2d',
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 16,
                      }}>
                        {selected.responder_name?.[0] || '?'}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{selected.responder_name}</div>
                        <div style={{ fontSize: 12, color: '#888' }}>
                          {selected.responder_role}
                          {selected.responder_email && ` · ${selected.responder_email}`}
                          {selected.responder_phone && ` · ${selected.responder_phone}`}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <StatusBadge status={selected.status} />
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                      Responded {fmtDate(selected.created_at)}
                    </div>
                  </div>
                </div>

                {/* Property details grid */}
                <div style={{
                  background: '#f8f8f8', borderRadius: 10, padding: '14px 16px',
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px 20px',
                  marginBottom: 12,
                }}>
                  {selected.property_address && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={detailLabelStyle}>📍 Property Address</div>
                      <div style={detailValueStyle}>{selected.property_address}</div>
                    </div>
                  )}
                  {selected.asking_price && (
                    <div>
                      <div style={detailLabelStyle}>💰 Asking Price</div>
                      <div style={{ ...detailValueStyle, color: '#2d6a2d', fontSize: 16 }}>{fmt(selected.asking_price)}</div>
                    </div>
                  )}
                  {selected.property_type && (
                    <div>
                      <div style={detailLabelStyle}>🏠 Type</div>
                      <div style={detailValueStyle}>{selected.property_type}</div>
                    </div>
                  )}
                  {selected.bedrooms != null && (
                    <div>
                      <div style={detailLabelStyle}>🛏 Bedrooms</div>
                      <div style={detailValueStyle}>{selected.bedrooms}</div>
                    </div>
                  )}
                  {selected.bathrooms != null && (
                    <div>
                      <div style={detailLabelStyle}>🚿 Bathrooms</div>
                      <div style={detailValueStyle}>{selected.bathrooms}</div>
                    </div>
                  )}
                  {selected.land_sqm != null && (
                    <div>
                      <div style={detailLabelStyle}>📐 Land Size</div>
                      <div style={detailValueStyle}>{selected.land_sqm.toLocaleString()} m²</div>
                    </div>
                  )}
                </div>

                {/* Initial message */}
                {selected.message && (
                  <div style={{
                    background: '#f0f7ff', borderRadius: 8, padding: '10px 14px',
                    fontSize: 13, color: '#444', lineHeight: 1.6, borderLeft: '3px solid #1a5baa',
                  }}>
                    <strong>Initial message:</strong> {selected.message}
                  </div>
                )}

                {/* Contact + Action buttons */}
                <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                  {selected.responder_email && (
                    <a
                      href={`mailto:${selected.responder_email}?subject=Re: EOI - ${eoi.title}`}
                      style={{
                        padding: '7px 14px', borderRadius: 8, border: '1.5px solid #ddd',
                        background: '#fff', color: '#333', fontSize: 12, fontWeight: 600,
                        textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      ✉️ Email Agent
                    </a>
                  )}
                  {selected.responder_phone && (
                    <a
                      href={`tel:${selected.responder_phone}`}
                      style={{
                        padding: '7px 14px', borderRadius: 8, border: '1.5px solid #ddd',
                        background: '#fff', color: '#333', fontSize: 12, fontWeight: 600,
                        textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      📞 Call
                    </a>
                  )}

                  {/* Buyer status action buttons */}
                  {actions?.map(a => (
                    <button
                      key={a.status}
                      onClick={() => handleStatusChange(a.status)}
                      style={{
                        padding: '7px 14px', borderRadius: 8,
                        border: `1.5px solid ${a.color}`,
                        background: a.bg, color: a.color,
                        fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status progress bar */}
              <StatusProgress status={selected.status} />

              {/* Chat thread */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: '#f7f9f7' }}>
                {loadingMsgs && (
                  <div style={{ textAlign: 'center', color: '#aaa', padding: 20 }}>Loading messages…</div>
                )}
                {!loadingMsgs && messages.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#aaa', padding: 30, fontSize: 13 }}>
                    No messages yet. Send a message to start the conversation.
                  </div>
                )}
                {messages.map(m => (
                  <ChatBubble key={m.id} msg={m} />
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* Message composer */}
              {selected.status !== 'closed' && (
                <form
                  onSubmit={handleSend}
                  style={{
                    padding: '12px 20px', borderTop: '1px solid #e8e8e8',
                    background: '#fff', display: 'flex', gap: 10,
                  }}
                >
                  <input
                    style={{
                      flex: 1, padding: '10px 14px', border: '1.5px solid #ddd',
                      borderRadius: 24, fontSize: 14, outline: 'none',
                    }}
                    placeholder="Type a message…"
                    value={msgBody}
                    onChange={e => setMsgBody(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend(e)}
                  />
                  <button
                    type="submit"
                    disabled={sending || !msgBody.trim()}
                    style={{
                      padding: '10px 20px', borderRadius: 24, border: 'none',
                      background: '#2d6a2d', color: '#fff', fontWeight: 700,
                      fontSize: 13, cursor: 'pointer', opacity: sending || !msgBody.trim() ? 0.5 : 1,
                    }}
                  >
                    {sending ? '…' : 'Send'}
                  </button>
                </form>
              )}
              {selected.status === 'closed' && (
                <div style={{
                  padding: '12px 20px', borderTop: '1px solid #e8e8e8',
                  background: '#fff8f8', textAlign: 'center', color: '#c0392b', fontSize: 13,
                }}>
                  This match is closed. No further messages can be sent.
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusProgress({ status }) {
  const steps = ['new', 'interested', 'details_requested', 'negotiating'];
  const idx = steps.indexOf(status);
  if (status === 'closed') {
    return (
      <div style={{
        padding: '8px 20px', background: '#fff0f0', fontSize: 12,
        color: '#c0392b', fontWeight: 600, borderBottom: '1px solid #f0f0f0',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        🔴 Interest withdrawn — this match is closed
      </div>
    );
  }
  return (
    <div style={{
      padding: '10px 20px', background: '#f9f9f9', borderBottom: '1px solid #f0f0f0',
      display: 'flex', alignItems: 'center', gap: 0,
    }}>
      {steps.map((s, i) => {
        const cfg = STATUS_CFG[s];
        const active = i <= idx;
        return (
          <React.Fragment key={s}>
            <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 3 }}>
              <div style={{
                width: 12, height: 12, borderRadius: 6,
                background: active ? cfg.color : '#ddd',
                border: `2px solid ${active ? cfg.color : '#ddd'}`,
              }} />
              <div style={{ fontSize: 9, color: active ? cfg.color : '#ccc', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {cfg.label}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 1, height: 2, background: i < idx ? '#2d6a2d' : '#e8e8e8', margin: '0 4px', marginBottom: 14,
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ChatBubble({ msg }) {
  const isMine = msg.is_mine;
  return (
    <div style={{
      display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', marginBottom: 12,
    }}>
      {!isMine && (
        <div style={{
          width: 30, height: 30, borderRadius: 15, background: '#1a5baa',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700, marginRight: 8, flexShrink: 0, alignSelf: 'flex-end',
        }}>
          {msg.sender_name?.[0] || '?'}
        </div>
      )}
      <div style={{ maxWidth: '70%' }}>
        {!isMine && (
          <div style={{ fontSize: 10, color: '#888', marginBottom: 3, paddingLeft: 2 }}>
            {msg.sender_name} · {msg.sender_role}
          </div>
        )}
        <div style={{
          padding: '10px 14px', borderRadius: isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          background: isMine ? '#2d6a2d' : '#fff',
          color: isMine ? '#fff' : '#1a1a1a',
          fontSize: 13, lineHeight: 1.5,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          {msg.body}
        </div>
        <div style={{ fontSize: 10, color: '#aaa', marginTop: 3, textAlign: isMine ? 'right' : 'left', paddingLeft: isMine ? 0 : 2 }}>
          {fmtDate(msg.created_at)} {fmtTime(msg.created_at)}
        </div>
      </div>
    </div>
  );
}

// ── Agent / Seller: My Responses Panel ───────────────────────────────────────
function MyResponsesPanel({ onClose }) {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgBody, setMsgBody] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    eoiAPI.getMyResponses().then(r => {
      setResponses(r.data);
      if (r.data.length > 0) setSelected(r.data[0]);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    eoiAPI.listMessages(selected.eoi_id, selected.id).then(r => {
      setMessages(r.data);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    });
  }, [selected?.id]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!msgBody.trim() || !selected) return;
    setSending(true);
    try {
      const r = await eoiAPI.sendMessage(selected.eoi_id, selected.id, msgBody.trim());
      setMessages(prev => [...prev, r.data]);
      setMsgBody('');
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch {}
    setSending(false);
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 900, width: '95%' }}>
        <div className="modal-head" style={{
          background: '#1a5baa', color: '#fff',
          borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>My Property Responses</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>EOIs you've responded to</div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
            borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14,
          }}>✕ Close</button>
        </div>

        <div className="master-detail">
          {/* Response list */}
          <div className="master-panel" style={{ background: '#fafafa' }}>
            {loading && <div style={{ padding: 20, color: '#888', textAlign: 'center' }}>Loading…</div>}
            {!loading && responses.length === 0 && (
              <div style={{ padding: 24, color: '#888', textAlign: 'center', fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📤</div>
                You haven't responded to any EOIs yet
              </div>
            )}
            {responses.map(m => (
              <div key={m.id} onClick={() => { setSelected(m); setMessages([]); }}
                style={{
                  padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                  background: selected?.id === m.id ? '#e8f0ff' : '#fff',
                  borderLeft: selected?.id === m.id ? '3px solid #1a5baa' : '3px solid transparent',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>{m.property_address || 'Property Response'}</div>
                {m.asking_price && (
                  <div style={{ fontSize: 12, color: '#2d6a2d', fontWeight: 600, marginTop: 2 }}>{fmt(m.asking_price)}</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                  <StatusBadge status={m.status} />
                  {m.unread_messages > 0 && (
                    <span style={{ background: '#1a5baa', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>
                      {m.unread_messages} new
                    </span>
                  )}
                </div>
                {m.total_messages > 0 && (
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>💬 {m.total_messages} messages</div>
                )}
              </div>
            ))}
          </div>

          {/* Chat */}
          <div className="detail-panel">
          {!selected ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#aaa' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>👈</div>
                <div>Select a response to chat</div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              {/* Property summary */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0', background: '#f8f8f8' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{selected.property_address || 'Your Property'}</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      {[selected.asking_price && fmt(selected.asking_price), selected.property_type, selected.bedrooms != null && `${selected.bedrooms} bed`, selected.bathrooms != null && `${selected.bathrooms} bath`].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <StatusBadge status={selected.status} />
                </div>
              </div>

              <StatusProgress status={selected.status} />

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: '#f7f9f7' }}>
                {messages.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#aaa', padding: 30, fontSize: 13 }}>
                    Send a message to start the conversation with the buyer.
                  </div>
                )}
                {messages.map(m => <ChatBubble key={m.id} msg={m} />)}
                <div ref={chatEndRef} />
              </div>

              {selected.status !== 'closed' ? (
                <form onSubmit={handleSend} style={{ padding: '12px 20px', borderTop: '1px solid #e8e8e8', background: '#fff', display: 'flex', gap: 10 }}>
                  <input
                    style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #ddd', borderRadius: 24, fontSize: 14, outline: 'none' }}
                    placeholder="Message the buyer…"
                    value={msgBody}
                    onChange={e => setMsgBody(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend(e)}
                  />
                  <button type="submit" disabled={sending || !msgBody.trim()} style={{
                    padding: '10px 20px', borderRadius: 24, border: 'none',
                    background: '#1a5baa', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    opacity: sending || !msgBody.trim() ? 0.5 : 1,
                  }}>
                    {sending ? '…' : 'Send'}
                  </button>
                </form>
              ) : (
                <div style={{ padding: '12px 20px', borderTop: '1px solid #e8e8e8', background: '#fff8f8', textAlign: 'center', color: '#c0392b', fontSize: 13 }}>
                  Buyer has withdrawn interest — this match is closed.
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── EOI Card ─────────────────────────────────────────────────────────────────
function EOICard({ eoi, onRespond, onView, onEdit, onDeactivate }) {
  const { user } = useAuth();
  const isOwner = eoi.is_mine;
  const canRespond = !isOwner && (user?.role === 'agent' || user?.role === 'owner' || user?.role === 'pro');

  const budgetStr = eoi.min_budget || eoi.max_budget
    ? `${eoi.min_budget ? fmt(eoi.min_budget) : 'Up to'} – ${eoi.max_budget ? fmt(eoi.max_budget) : 'open'}`
    : 'Budget flexible';

  const locationStr = [eoi.suburb, eoi.state, eoi.postcode].filter(Boolean).join(', ') || 'Location flexible';

  return (
    <div style={{
      background: '#fff', border: '1px solid #e8e8e8', borderRadius: 12,
      padding: 20, marginBottom: 16,
      boxShadow: isOwner ? '0 0 0 2px #2d6a2d22' : 'none',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{eoi.title}</h3>
            {isOwner && <Chip label="My EOI" color="#e8f4e8" textColor="#2d6a2d" />}
            {!eoi.is_active && <Chip label="Inactive" color="#f5f5f5" textColor="#888" />}
            {eoi.is_anonymous && !isOwner && <Chip label="Anonymous" color="#f0f0f0" textColor="#666" />}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {eoi.property_types?.map(t => (
              <Chip key={t} label={t.charAt(0).toUpperCase() + t.slice(1)} color="#f0f7ff" textColor="#1a5baa" />
            ))}
            {eoi.condition && eoi.condition !== 'any' && (
              <Chip label={eoi.condition} color="#fff8e8" textColor="#a06000" />
            )}
          </div>
        </div>

        {isOwner && (
          <div onClick={() => onView(eoi)} style={{
            cursor: 'pointer', textAlign: 'center', minWidth: 64,
            background: eoi.unread_count > 0 ? '#2d6a2d' : '#f5f5f5',
            color: eoi.unread_count > 0 ? '#fff' : '#666',
            borderRadius: 10, padding: '8px 12px',
          }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{eoi.match_count}</div>
            <div style={{ fontSize: 10, fontWeight: 600 }}>
              {eoi.unread_count > 0 ? `${eoi.unread_count} new` : 'matches'}
            </div>
          </div>
        )}
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: '8px 16px', margin: '14px 0', padding: '12px 0',
        borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0',
      }}>
        <MiniDetail icon="📍" label="Location" value={locationStr} />
        <MiniDetail icon="💰" label="Budget" value={budgetStr} />
        {eoi.min_bedrooms != null && <MiniDetail icon="🛏" label="Min Beds" value={`${eoi.min_bedrooms}+`} />}
        {eoi.min_bathrooms != null && <MiniDetail icon="🚿" label="Min Baths" value={`${eoi.min_bathrooms}+`} />}
        {eoi.min_land_sqm != null && <MiniDetail icon="📐" label="Min Land" value={`${eoi.min_land_sqm.toLocaleString()} m²`} />}
      </div>

      {eoi.description && (
        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#555', lineHeight: 1.5 }}>{eoi.description}</p>
      )}
      {eoi.features_wanted?.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>WANTS: </span>
          {eoi.features_wanted.map(f => <Chip key={f} label={f} color="#f5f0ff" textColor="#5a2d9e" />)}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <div style={{ fontSize: 12, color: '#aaa' }}>
          {eoi.is_anonymous && !isOwner ? 'Buyer: Anonymous'
            : eoi.buyer_name ? `Buyer: ${eoi.buyer_name}` : ''}
          {eoi.expires_at && <span style={{ marginLeft: 10 }}>Expires: {fmtDate(eoi.expires_at)}</span>}
          <span style={{ marginLeft: 10 }}>Posted {fmtDate(eoi.created_at)}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isOwner && (
            <>
              <button className="btn ghost sm" onClick={() => onView(eoi)}>
                View Matches {eoi.unread_count > 0 && `(${eoi.unread_count} new)`}
              </button>
              <button className="btn ghost sm" onClick={() => onEdit(eoi)}>Edit</button>
              <button className="btn ghost sm" onClick={() => onDeactivate(eoi)}
                style={{ color: eoi.is_active ? '#c0392b' : '#2d6a2d' }}>
                {eoi.is_active ? 'Deactivate' : 'Reactivate'}
              </button>
            </>
          )}
          {canRespond && (
            <button className="btn primary sm" onClick={() => onRespond(eoi)}>
              Respond with Property
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniDetail({ icon, label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginTop: 2 }}>{value}</div>
    </div>
  );
}

// ── EOI Create / Edit Modal ───────────────────────────────────────────────────
function EOIModal({ eoi, onClose, onSaved }) {
  const isEdit = !!eoi;
  const [form, setForm] = useState({
    title: eoi?.title || '',
    is_anonymous: eoi?.is_anonymous || false,
    property_types: eoi?.property_types || [],
    min_budget: eoi?.min_budget || '',
    max_budget: eoi?.max_budget || '',
    suburb: eoi?.suburb || '',
    state: eoi?.state || '',
    postcode: eoi?.postcode || '',
    min_bedrooms: eoi?.min_bedrooms || '',
    min_bathrooms: eoi?.min_bathrooms || '',
    min_land_sqm: eoi?.min_land_sqm || '',
    min_floor_sqm: eoi?.min_floor_sqm || '',
    condition: eoi?.condition || 'any',
    features_wanted: eoi?.features_wanted || [],
    description: eoi?.description || '',
    expires_at: eoi?.expires_at || '',
    // Financial benefits
    eligible_for_negative_gearing: eoi?.eligible_for_negative_gearing ?? null,
    min_rental_yield: eoi?.min_rental_yield || '',
    min_capital_growth_yoy: eoi?.min_capital_growth_yoy || '',
    subdivision_potential: eoi?.subdivision_potential ?? null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleList = (k, v) => setForm(f => ({
    ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v],
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        min_budget: form.min_budget ? Number(form.min_budget) : null,
        max_budget: form.max_budget ? Number(form.max_budget) : null,
        suburb: form.suburb || null, state: form.state || null, postcode: form.postcode || null,
        min_bedrooms: form.min_bedrooms ? Number(form.min_bedrooms) : null,
        min_bathrooms: form.min_bathrooms ? Number(form.min_bathrooms) : null,
        min_land_sqm: form.min_land_sqm ? Number(form.min_land_sqm) : null,
        min_floor_sqm: form.min_floor_sqm ? Number(form.min_floor_sqm) : null,
        condition: form.condition || null, description: form.description || null,
        expires_at: form.expires_at || null,
        eligible_for_negative_gearing: form.eligible_for_negative_gearing,
        min_rental_yield: form.min_rental_yield ? Number(form.min_rental_yield) : null,
        min_capital_growth_yoy: form.min_capital_growth_yoy ? Number(form.min_capital_growth_yoy) : null,
        subdivision_potential: form.subdivision_potential,
      };
      const resp = isEdit ? await eoiAPI.update(eoi.id, payload) : await eoiAPI.create(payload);
      onSaved(resp.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const chipBtn = (active, onClick, label, activeColor = '#2d6a2d', activeBg = '#e8f4e8') => (
    <button type="button" onClick={onClick} style={{
      padding: '5px 13px', borderRadius: 20, border: `1.5px solid ${active ? activeColor : '#ddd'}`,
      background: active ? activeBg : '#fff', color: active ? activeColor : '#555',
      fontSize: 12, cursor: 'pointer', fontWeight: 500,
    }}>{label}</button>
  );

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-head">
          <h2 style={{ margin: 0, fontSize: 18 }}>{isEdit ? 'Edit EOI' : 'Post Expression of Interest'}</h2>
          <button className="btn ghost sm" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>EOI Title *</label>
          <input style={inputStyle} placeholder="e.g. Looking for a 4-bed family home in Brisbane North"
            value={form.title} onChange={e => set('title', e.target.value)} required />

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_anonymous} onChange={e => set('is_anonymous', e.target.checked)} />
            <span style={{ fontSize: 14 }}><strong>Post anonymously</strong> — hide your contact details from agents</span>
          </label>

          <label style={labelStyle}>Property Type(s)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {PROPERTY_TYPES.map(t => chipBtn(form.property_types.includes(t), () => toggleList('property_types', t), t.charAt(0).toUpperCase() + t.slice(1)))}
          </div>

          <label style={labelStyle}>Budget Range</label>
          <div className="r-cols-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Min ($)</div>
              <input style={inputStyle} type="number" placeholder="500,000" value={form.min_budget} onChange={e => set('min_budget', e.target.value)} /></div>
            <div><div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Max ($)</div>
              <input style={inputStyle} type="number" placeholder="800,000" value={form.max_budget} onChange={e => set('max_budget', e.target.value)} /></div>
          </div>

          <label style={labelStyle}>Location</label>
          <div className="r-cols-addr" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <input style={inputStyle} placeholder="Suburb / Area" value={form.suburb} onChange={e => set('suburb', e.target.value)} />
            <select style={inputStyle} value={form.state} onChange={e => set('state', e.target.value)}>
              <option value="">State</option>
              {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input style={inputStyle} placeholder="Postcode" value={form.postcode} onChange={e => set('postcode', e.target.value)} />
          </div>

          <label style={labelStyle}>Property Requirements</label>
          <div className="r-cols-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 8 }}>
            {[['min_bedrooms', 'Min Beds'], ['min_bathrooms', 'Min Baths'], ['min_land_sqm', 'Min Land m²'], ['min_floor_sqm', 'Min Floor m²']].map(([k, l]) => (
              <div key={k}><div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{l}</div>
                <input style={inputStyle} type="number" min="0" placeholder="Any" value={form[k]} onChange={e => set(k, e.target.value)} /></div>
            ))}
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Condition</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {CONDITIONS.map(c => chipBtn(form.condition === c, () => set('condition', c), c.charAt(0).toUpperCase() + c.slice(1)))}
            </div>
          </div>

          <label style={labelStyle}>Desired Features</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {FEATURES.map(f => chipBtn(form.features_wanted.includes(f), () => toggleList('features_wanted', f), f, '#5a2d9e', '#f5f0ff'))}
          </div>

          <label style={labelStyle}>Additional Details</label>
          <textarea style={{ ...inputStyle, height: 90, resize: 'vertical' }}
            placeholder="School zones, preferred street type, settlement timeline…"
            value={form.description} onChange={e => set('description', e.target.value)} />

          {/* ── Financial Benefits ── */}
          <div style={{ margin: '20px 0 4px', padding: '16px 18px', background: 'rgba(31,90,31,0.04)', borderRadius: 10, border: '1px solid rgba(31,90,31,0.12)' }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#2d6a2d', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              💰 Financial Benefits
              <span style={{ fontWeight: 400, fontSize: 11, color: '#666' }}>— investment criteria you're looking for</span>
            </div>

            <div className="r-cols-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>Rental Yield (min %)</div>
                <input style={inputStyle} type="number" step="0.1" min="0" max="30"
                  placeholder="e.g. 5.5"
                  value={form.min_rental_yield}
                  onChange={e => set('min_rental_yield', e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>Capital Growth YoY (min %)</div>
                <input style={inputStyle} type="number" step="0.1" min="0" max="50"
                  placeholder="e.g. 8.0"
                  value={form.min_capital_growth_yoy}
                  onChange={e => set('min_capital_growth_yoy', e.target.value)} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={form.eligible_for_negative_gearing === true}
                  onChange={e => set('eligible_for_negative_gearing', e.target.checked ? true : null)}
                />
                Eligible for Negative Gearing
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={form.subdivision_potential === true}
                  onChange={e => set('subdivision_potential', e.target.checked ? true : null)}
                />
                Subdivision Potential
              </label>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Expires On (optional)</label>
            <input style={{ ...inputStyle, width: 200 }} type="date" value={form.expires_at} onChange={e => set('expires_at', e.target.value)} />
          </div>

          {error && <div style={{ color: '#c0392b', marginTop: 12, fontSize: 13 }}>{error}</div>}
        </form>
        </div>

        <div className="modal-foot">
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Post EOI'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Respond Modal ─────────────────────────────────────────────────────────────
function RespondModal({ eoi, onClose, onSaved }) {
  const [form, setForm] = useState({ property_address: '', asking_price: '', property_type: '', bedrooms: '', bathrooms: '', land_sqm: '', message: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.property_address.trim() && !form.message.trim()) {
      setError('Provide at least a property address or a message'); return;
    }
    setSaving(true); setError('');
    try {
      const resp = await eoiAPI.createMatch(eoi.id, {
        property_address: form.property_address || null,
        asking_price: form.asking_price ? Number(form.asking_price) : null,
        property_type: form.property_type || null,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
        land_sqm: form.land_sqm ? Number(form.land_sqm) : null,
        message: form.message || null,
      });
      onSaved(resp.data);
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-head">
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>Respond to EOI</h2>
            <div style={{ fontSize: 13, color: 'var(--ink-4)', marginTop: 4 }}>"{eoi.title}"</div>
          </div>
          <button className="btn ghost sm" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
        <form onSubmit={handleSubmit}>
          <div style={{ background: '#f8f4ff', borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13, color: '#444', borderLeft: '3px solid #7b4fc9' }}>
            <strong>Buyer wants:</strong>{' '}
            {eoi.property_types?.join(', ') || 'any type'}
            {eoi.suburb && ` · ${eoi.suburb}`}{eoi.state && `, ${eoi.state}`}
            {eoi.max_budget && ` · up to ${fmt(eoi.max_budget)}`}
            {eoi.min_bedrooms && ` · ${eoi.min_bedrooms}+ bed`}
          </div>

          <label style={labelStyle}>Property Address</label>
          <input style={inputStyle} placeholder="42 Oak St, Paddington QLD 4064"
            value={form.property_address} onChange={e => set('property_address', e.target.value)} />

          <div className="r-cols-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div><div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Asking Price ($)</div>
              <input style={inputStyle} type="number" placeholder="750,000" value={form.asking_price} onChange={e => set('asking_price', e.target.value)} /></div>
            <div><div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Property Type</div>
              <select style={inputStyle} value={form.property_type} onChange={e => set('property_type', e.target.value)}>
                <option value="">Select…</option>
                {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select></div>
          </div>

          <div className="r-cols-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 12 }}>
            {[['bedrooms', 'Bedrooms'], ['bathrooms', 'Bathrooms'], ['land_sqm', 'Land (m²)']].map(([k, l]) => (
              <div key={k}><div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{l}</div>
                <input style={inputStyle} type="number" min="0" placeholder="—" value={form[k]} onChange={e => set(k, e.target.value)} /></div>
            ))}
          </div>

          <label style={{ ...labelStyle, marginTop: 16 }}>Message to Buyer</label>
          <textarea style={{ ...inputStyle, height: 100, resize: 'vertical' }}
            placeholder="Why this property matches their requirements, inspection availability…"
            value={form.message} onChange={e => set('message', e.target.value)} />

          {error && <div style={{ color: '#c0392b', marginTop: 10, fontSize: 13 }}>{error}</div>}
        </form>
        </div>

        <div className="modal-foot">
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Sending…' : 'Send Response'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Filter Bar ────────────────────────────────────────────────────────────────
function FilterBar({ filters, onChange }) {
  const set = (k, v) => onChange({ ...filters, [k]: v });
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      {[['property_type', 'TYPE', null], ['state', 'STATE', null]].map(([k, l]) => (
        <div key={k}><div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4 }}>{l}</div>
          <select style={{ ...inputStyle, width: k === 'property_type' ? 140 : 100 }} value={filters[k]} onChange={e => set(k, e.target.value)}>
            <option value="">{k === 'property_type' ? 'All types' : 'All'}</option>
            {(k === 'property_type' ? PROPERTY_TYPES : AU_STATES).map(v => (
              <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
            ))}
          </select></div>
      ))}
      <div><div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4 }}>SUBURB</div>
        <input style={{ ...inputStyle, width: 150 }} placeholder="Any" value={filters.suburb} onChange={e => set('suburb', e.target.value)} /></div>
      <div><div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4 }}>MAX BUDGET</div>
        <input style={{ ...inputStyle, width: 120 }} type="number" placeholder="No limit" value={filters.max_budget} onChange={e => set('max_budget', e.target.value)} /></div>
      <div><div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4 }}>MIN BEDS</div>
        <input style={{ ...inputStyle, width: 80 }} type="number" min="0" placeholder="Any" value={filters.min_bedrooms} onChange={e => set('min_bedrooms', e.target.value)} /></div>
      <button className="btn ghost sm" onClick={() => onChange({ property_type: '', state: '', suburb: '', max_budget: '', min_bedrooms: '' })}>Clear</button>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function EOIMarketplacePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('browse');
  const [eois, setEois] = useState([]);
  const [myEois, setMyEois] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ property_type: '', state: '', suburb: '', max_budget: '', min_bedrooms: '' });

  const [showCreate, setShowCreate] = useState(false);
  const [editEoi, setEditEoi] = useState(null);
  const [respondEoi, setRespondEoi] = useState(null);
  const [viewMatchesEoi, setViewMatchesEoi] = useState(null);
  const [showMyResponses, setShowMyResponses] = useState(false);

  const canRespond = user?.role === 'agent' || user?.role === 'owner' || user?.role === 'pro';

  const loadBrowse = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (filters.property_type) params.property_type = filters.property_type;
      if (filters.state) params.state = filters.state;
      if (filters.suburb) params.suburb = filters.suburb;
      if (filters.max_budget) params.max_budget = Number(filters.max_budget);
      if (filters.min_bedrooms) params.min_bedrooms = Number(filters.min_bedrooms);
      const r = await eoiAPI.list(params);
      setEois(r.data.items);
      setTotal(r.data.total);
    } finally { setLoading(false); }
  }, [page, filters]);

  const loadMine = useCallback(async () => {
    const r = await eoiAPI.getMine();
    setMyEois(r.data);
  }, []);

  useEffect(() => { loadBrowse(); }, [loadBrowse]);
  useEffect(() => { loadMine(); }, [loadMine]);

  const refresh = () => { loadBrowse(); loadMine(); };

  const handleDeactivate = async (eoi) => {
    try { await eoiAPI.update(eoi.id, { is_active: !eoi.is_active }); refresh(); } catch {}
  };

  const totalPages = Math.ceil(total / 20);
  const unreadTotal = myEois.reduce((sum, e) => sum + (e.unread_count || 0), 0);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Property EOI Marketplace</h1>
          <p style={{ margin: '6px 0 0', color: '#666', fontSize: 14 }}>
            Buyers post requirements · Agents and sellers respond with matching properties
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {canRespond && (
            <button className="btn ghost" onClick={() => setShowMyResponses(true)}>
              My Responses
            </button>
          )}
          <button className="btn primary" onClick={() => setShowCreate(true)}>+ Post EOI</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #f0f0f0', marginBottom: 20 }}>
        {[
          { key: 'browse', label: `Browse All (${total})` },
          { key: 'mine', label: `My EOIs (${myEois.length})${unreadTotal > 0 ? ` 🔔${unreadTotal}` : ''}` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            background: 'none', border: 'none',
            borderBottom: `2px solid ${tab === t.key ? '#2d6a2d' : 'transparent'}`,
            marginBottom: -2, padding: '10px 20px', cursor: 'pointer',
            color: tab === t.key ? '#2d6a2d' : '#666', fontWeight: tab === t.key ? 700 : 400, fontSize: 14,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'browse' && <FilterBar filters={filters} onChange={(f) => { setFilters(f); setPage(1); }} />}

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading…</div>}

      {!loading && (tab === 'browse' ? eois : myEois).length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, background: '#fafafa', borderRadius: 12, border: '1px dashed #e0e0e0', color: '#888' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{tab === 'mine' ? '📋' : '🔍'}</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            {tab === 'mine' ? 'No EOIs yet' : 'No matching EOIs'}
          </div>
          <div style={{ fontSize: 13 }}>
            {tab === 'mine' ? 'Post your requirements and let sellers come to you.' : 'Try adjusting filters.'}
          </div>
          {tab === 'mine' && (
            <button className="btn primary" style={{ marginTop: 16 }} onClick={() => setShowCreate(true)}>Post Your First EOI</button>
          )}
        </div>
      )}

      {(tab === 'browse' ? eois : myEois).map(eoi => (
        <EOICard key={eoi.id} eoi={eoi}
          onRespond={setRespondEoi}
          onView={setViewMatchesEoi}
          onEdit={setEditEoi}
          onDeactivate={handleDeactivate}
        />
      ))}

      {tab === 'browse' && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          <button className="btn ghost sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ padding: '6px 12px', fontSize: 13, color: '#666' }}>Page {page} of {totalPages}</span>
          <button className="btn ghost sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {showCreate && <EOIModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refresh(); }} />}
      {editEoi && <EOIModal eoi={editEoi} onClose={() => setEditEoi(null)} onSaved={() => { setEditEoi(null); refresh(); }} />}
      {respondEoi && <RespondModal eoi={respondEoi} onClose={() => setRespondEoi(null)} onSaved={() => { setRespondEoi(null); refresh(); }} />}
      {viewMatchesEoi && <MatchConversationPanel eoi={viewMatchesEoi} onClose={() => { setViewMatchesEoi(null); refresh(); }} onMatchUpdated={loadMine} />}
      {showMyResponses && <MyResponsesPanel onClose={() => setShowMyResponses(false)} />}
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, color: '#555',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, marginTop: 16,
};
const inputStyle = {
  width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: 8,
  fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#fff',
};
const detailLabelStyle = { fontSize: 10, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 };
const detailValueStyle = { fontSize: 13, fontWeight: 600, color: '#222' };
